<script lang="ts">
  import { navigate, currentRoute } from 'sly-svelte-location-router'

  const navLinks = [
    { path: '/design-system', label: 'Design System' },
    { path: '/logo', label: 'Logo' },
    { path: '/colors', label: 'Colors' },
    { path: '/typography', label: 'Typography' },
    { path: '/graphics', label: 'Graphics' },
  ]

  let mobileOpen = $state(false)
  let dark = $state(
    typeof window !== 'undefined' && localStorage.getItem('theme') === 'dark'
  )

  $effect(() => {
    document.documentElement.classList.toggle('dark', dark)
    localStorage.setItem('theme', dark ? 'dark' : 'light')
  })

  const isActive = (path: string) => {
    const current = $currentRoute.path
    if (path === '/design-system') {
      return current === '/' || current === '/design-system'
    }
    return current === path
  }

  const handleNav = (path: string) => {
    navigate(path)
    mobileOpen = false
  }
</script>

<nav class="fixed top-0 left-0 right-0 z-50 bg-navy">
  <div class="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
    <button class="flex items-center gap-3 cursor-pointer" onclick={() => handleNav('/design-system')}>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 4226 3336" class="h-6 w-auto">
        <path stroke="#ffffff" stroke-linecap="round" stroke-width="901" d="M466.086 455.621 2844.62 2868.91"/>
        <path fill="#ffffff" d="M2390.94 2845.69c.15 248.8 201.96 450.38 450.77 450.23 248.8-.14 450.38-201.96 450.23-450.76l-901 .53Zm899.59-2394.956L3290.26.233943l-901 .531368.27 450.499689 901-.531Zm1.41 2394.426-1.41-2394.426-901 .531 1.41 2394.425 901-.53Z"/>
        <path stroke="#ffffff" stroke-linecap="square" stroke-width="901" d="M3775 451h-935"/>
        <circle cx="479.5" cy="2856.5" r="479.5" fill="#ffffff"/>
      </svg>
      <span class="font-title font-semibold text-white text-lg tracking-wide">nimtech</span>
    </button>

    <div class="hidden md:flex items-center gap-8">
      {#each navLinks as link}
        <button
          class="font-body font-semibold text-sm tracking-wide transition-colors cursor-pointer"
          class:text-coral={isActive(link.path)}
          class:text-white={!isActive(link.path)}
          class:opacity-70={!isActive(link.path)}
          class:hover:opacity-100={!isActive(link.path)}
          onclick={() => handleNav(link.path)}
        >
          {link.label}
        </button>
      {/each}

      <button
        class="text-white/70 hover:text-white transition-colors cursor-pointer p-1"
        onclick={() => dark = !dark}
        aria-label="Toggle theme"
      >
        {#if dark}
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        {:else}
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        {/if}
      </button>
    </div>

    <div class="md:hidden flex items-center gap-4">
      <button
        class="text-white/70 hover:text-white transition-colors cursor-pointer p-1"
        onclick={() => dark = !dark}
        aria-label="Toggle theme"
      >
        {#if dark}
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        {:else}
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        {/if}
      </button>

      <button
        class="text-white cursor-pointer"
        onclick={() => mobileOpen = !mobileOpen}
        aria-label="Toggle menu"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          {#if mobileOpen}
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          {:else}
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          {/if}
        </svg>
      </button>
    </div>
  </div>

  {#if mobileOpen}
    <div class="md:hidden bg-navy border-t border-white/10 px-6 pb-4">
      {#each navLinks as link}
        <button
          class="block w-full text-left py-3 font-body font-semibold text-sm tracking-wide transition-colors cursor-pointer"
          class:text-coral={isActive(link.path)}
          class:text-white={!isActive(link.path)}
          class:opacity-70={!isActive(link.path)}
          onclick={() => handleNav(link.path)}
        >
          {link.label}
        </button>
      {/each}
    </div>
  {/if}
</nav>
